import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Images, ChevronRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { Card } from "@/components/ui/card";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";

const AUTO_ADVANCE_MS = 4000;

/**
 * Auto-advancing slideshow of all photos in the public gallery. Pictures stay
 * in sync with /api/photos so any image the admin uploads via the Fotos page
 * shows up here automatically. Hidden entirely when the gallery is empty.
 */
export function GallerySlideshow() {
  const { t, lang } = useI18n();
  const [photos, setPhotos] = useState(null); // null = loading, [] = empty
  const [api_, setApi] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/photos");
        if (!cancelled) setPhotos(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setPhotos([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-advance every 4 seconds.
  useEffect(() => {
    if (!api_ || !photos || photos.length < 2) return;
    const id = setInterval(() => {
      try { api_.scrollNext(); } catch (_e) { /* embla not ready */ }
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [api_, photos]);

  // Track which slide is active so we can highlight the dot indicator.
  useEffect(() => {
    if (!api_) return;
    const onSelect = () => {
      try { setSelectedIndex(api_.selectedScrollSnap()); } catch (_e) { /* noop */ }
    };
    api_.on("select", onSelect);
    api_.on("reInit", onSelect);
    onSelect();
    return () => {
      try {
        api_.off("select", onSelect);
        api_.off("reInit", onSelect);
      } catch (_e) { /* embla teardown */ }
    };
  }, [api_]);

  if (photos === null) {
    // loading: render a placeholder card so layout doesn't jump.
    return (
      <div className="rounded-2xl bg-muted/50 ring-1 ring-black/5 aspect-[16/9] animate-pulse" />
    );
  }

  if (photos.length === 0) {
    // Per product decision: hide entirely when empty (no broken state).
    return null;
  }

  return (
    <section className="space-y-2" data-testid="home-gallery-slideshow">
      <div className="flex items-end justify-between gap-3 px-1">
        <div className="flex items-center gap-2">
          <Images className="h-4 w-4 text-primary" />
          <h2 className="font-serif-display text-lg font-semibold tracking-tight">{t.home.gallerySectionTitle}</h2>
        </div>
        <Link to="/gallery" className="text-xs font-medium text-primary inline-flex items-center gap-0.5" data-testid="home-gallery-view-all">
          {t.home.gallerySectionViewAll}<ChevronRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <Card className="relative overflow-hidden rounded-2xl ring-1 ring-black/5 shadow-sm">
        <Carousel
          opts={{ loop: photos.length > 1, align: "start" }}
          setApi={setApi}
          className="w-full"
        >
          <CarouselContent>
            {photos.map((p, i) => {
              const caption = lang === "es" ? (p.caption_es || "") : (p.caption_en || p.caption_es || "");
              return (
                <CarouselItem key={p.id} className="" data-testid={`home-gallery-slide-${p.id}`}>
                  <div className="relative aspect-[16/10] w-full bg-muted">
                    <img
                      src={p.image_url}
                      alt={caption || `Slide ${i + 1}`}
                      loading={i === 0 ? "eager" : "lazy"}
                      className="absolute inset-0 h-full w-full object-cover"
                    />
                    {caption && (
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/15 to-transparent px-3 pt-8 pb-2">
                        <p className="text-sm text-white drop-shadow line-clamp-2">{caption}</p>
                      </div>
                    )}
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {photos.length > 1 && (
            <>
              <CarouselPrevious className="left-2 size-8 bg-white/80 hover:bg-white" data-testid="home-gallery-prev" />
              <CarouselNext className="right-2 size-8 bg-white/80 hover:bg-white" data-testid="home-gallery-next" />
            </>
          )}
        </Carousel>
        {photos.length > 1 && (
          <div className="flex items-center justify-center gap-1.5 py-2.5" role="tablist" data-testid="home-gallery-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { try { api_?.scrollTo(i); } catch (_e) { /* embla not ready */ } }}
                aria-label={`Slide ${i + 1}`}
                aria-selected={selectedIndex === i}
                className={`h-1.5 rounded-full transition-all ${
                  selectedIndex === i ? "w-5 bg-primary" : "w-1.5 bg-muted-foreground/40 hover:bg-muted-foreground/70"
                }`}
                data-testid={`home-gallery-dot-${i}`}
              />
            ))}
          </div>
        )}
      </Card>
    </section>
  );
}
